// src/features/tutor/SelfLearningStatus.js
// Shows tutors, per lesson, whether each learner opened the lesson's units
// in the LMS before the session — so they can talk about self-learning at
// the start of the session.
//
// Tutors see only OPENED / NOT OPENED (decided 2026-10-12). The studied /
// skipped split stays admin-only, so tutors don't discourage skipping.
//
// "Opened" uses everything the LMS has recorded, so it also works for weeks
// before the studied/skipped tracking existed.
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const TEAL = '#19a4a0';
const YELLOW = '#b7791f'; // darker shade of #e8a327 for readable text
const GREY = '#6b7280';
const RED = '#ff4747';

// ── Data helpers ────────────────────────────────────────────────────────────

// Every unit id this learner has ever opened in the LMS.
export function openedIds(progress) {
  const ids = new Set();
  if (!progress) return ids;
  const vp = progress.visitedPackages;
  if (Array.isArray(vp)) vp.forEach(id => ids.add(id));
  else if (vp && typeof vp === 'object') Object.keys(vp).forEach(id => ids.add(id));
  (progress.visitedUnits || []).forEach(id => ids.add(id));
  Object.keys(progress.completedNavItems || {}).forEach(id => ids.add(id));
  Object.keys(progress.unitActivity || {}).forEach(id => ids.add(id));
  return ids;
}

// Has the learner ever used the LMS at all?
export function hasUsedLms(progress) {
  return openedIds(progress).size > 0;
}

// Load learnerProgress for a list of learners: { learnerId: data | null }.
export function useLearnersProgress(learnerIds) {
  const [progress, setProgress] = useState(null); // null = loading
  const key = learnerIds.join(',');

  useEffect(() => {
    let cancelled = false;
    if (learnerIds.length === 0) { setProgress({}); return undefined; }
    Promise.all(learnerIds.map(async id => {
      try {
        const snap = await getDoc(doc(db, 'learnerProgress', id));
        return [id, snap.exists() ? snap.data() : null];
      } catch (e) {
        console.warn('Could not read progress for', id, e);
        return [id, undefined]; // unknown (e.g. no permission)
      }
    })).then(entries => {
      if (!cancelled) setProgress(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return progress;
}

// The steps a learner sees in a lesson: packages + units that aren't inside
// a package. Cached so reopening a lesson doesn't fetch again.
const lessonStepsCache = new Map();

async function loadLessonStepIds(lessonId) {
  if (lessonStepsCache.has(lessonId)) return lessonStepsCache.get(lessonId);
  const [unitsSnap, packagesSnap] = await Promise.all([
    getDocs(query(collection(db, 'units'), where('lesson_id', '==', lessonId))),
    getDocs(query(collection(db, 'packages'), where('lessonId', '==', lessonId)))
  ]);
  const packages = packagesSnap.docs.map(d => ({ id: d.id, unitIds: d.data().unitIds || [] }));
  const inPackage = new Set(packages.flatMap(p => p.unitIds));
  const ids = [
    ...packages.map(p => p.id),
    ...unitsSnap.docs.map(d => d.id).filter(id => !inPackage.has(id))
  ];
  lessonStepsCache.set(lessonId, ids);
  return ids;
}

// ── Component ───────────────────────────────────────────────────────────────

function SelfLearningStatus({ lessonId, learners, progressByLearner }) {
  const [stepIds, setStepIds] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLessonStepIds(lessonId)
      .then(ids => { if (!cancelled) setStepIds(ids); })
      .catch(e => { console.warn('Could not load lesson steps:', e); if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [lessonId]);

  if (error) return null;

  const loading = !stepIds || !progressByLearner;
  const total = stepIds ? stepIds.length : 0;

  return (
    <div style={styles.box}>
      <span style={styles.label}>Self-learning:</span>
      {loading && <span style={{ color: GREY }}>checking…</span>}
      {!loading && learners.map(l => {
        const p = progressByLearner[l.id];
        let text;
        let color;
        if (p === undefined) {
          text = 'unknown';
          color = GREY;
        } else if (!hasUsedLms(p)) {
          text = "hasn't used the LMS yet";
          color = RED;
        } else {
          const opened = openedIds(p);
          const n = stepIds.filter(id => opened.has(id)).length;
          if (n === 0) { text = 'not opened yet'; color = GREY; }
          else if (n >= total) { text = `${n}/${total} opened`; color = TEAL; }
          else { text = `${n}/${total} opened`; color = YELLOW; }
        }
        return (
          <span key={l.id} style={styles.item}>
            <strong>{(l.name || '').split(' ')[0] || l.name}</strong>{' '}
            <span style={{ color }}>{text}</span>
          </span>
        );
      })}
    </div>
  );
}

const styles = {
  box: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px 14px',
    margin: '0 1rem 0.6rem', padding: '8px 12px', background: '#f9fafb',
    border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.85rem', color: '#374151'
  },
  label: { fontWeight: 600, color: '#0f766e' },
  item: { whiteSpace: 'nowrap' }
};

export default SelfLearningStatus;